<template>
  <div>
    <b-input-group>
      <b-form-input
        id="datepicker-input"
        v-model="displayDate"
        @blur="handleBlur"
        type="text"
        placeholder="mm-dd-yyyy"
        autocomplete="off"
        :disabled="disabled"
        :readonly="disabled"
        style="line-height: 1rem;"
      ></b-form-input>
      <b-input-group-append>
        <b-form-datepicker
          ref="datepicker"
          v-model="value"
          :disabled="disabled"
          :readonly="disabled"
          button-only
          right
          size="sm"
          locale="en-US"
          button-variant="white"
          aria-controls="example-input"
          :date-format-options="{ month: 'numeric', day: 'numeric', year: 'numeric'}"
          @context="onContext"
        ></b-form-datepicker>
      </b-input-group-append>
    </b-input-group>
  </div>
</template>
<script>
  import moment from 'moment'
  export default {
    name: 'DateField',
    props: {
      value: {
        'type': [ String, Number ],
        'required': false,
        'default': ''
      },
      name: {
        'type': String,
        'required': false,
        'default': ''
      },
      required: {
        'type': Boolean,
        'required': false,
        'default': false
      },
      disabled: {
        'type': Boolean,
        'required': false,
        'default': false
      },
      placeholder: {
        'type': String,
        'required': false
      },
      tabindex: {
        'type': String,
        'required': false,
        'default': ''
      },
      reference: {
        'type': String,
        'required': false,
        'default': ''
      },
      dataoptions: {
        'type': Object,
        'required': false
      },
      allowableRange: {
        'type': Object,
        'required': false,
        'validator': (value) => {
          return typeof value.start === 'number' && typeof value.end === 'number' && value.start < value.end
        }
      },
      customStyle: {
        'type': Object,
        'required': false,
        'default': () => {}
      }
    },
    data () {
      return {
        date: '',
        locale: window.navigator.userLanguage || window.navigator.language || 'en-US', // 'fr-fr' // test stuff,
        dateValue: ''
      }
    },
    methods: {
      /**
       * Handles focus event
       * @param e   FocusEvent
       */
      handleFocus (e) {
        this.$emit('focus', e)
      },
      /**
       * Handles user input. Fires an input event to update v-model, as well as a change event since the input
       * value as changed.
       */
      handleInput () {
        this.$emit('input', this.date)
        this.$emit('change')
      },
      /**
       * Handles blur event. Emits a blur event (standard), as well as an input event for v-model update after date is
       * formatted to the correct format. Also fires a change event since the value is changed after it is formatted.
       * @param e   FocusEvent
       */
      handleBlur (e) {
        /*
        let date = this.parseUserDateFormats(e.target.value)
        if (date && date.year) {
          this.value = new Date(date.year, date.month - 1, date.day)
        }
        */
        if (e.target.value) {
          var m = moment(e.target.value, 'MM-DD-YYYY')
          if (m.isValid()) {
            let date = this.parseUserDateFormats(e.target.value)
            this.value = new Date(date.year, (date.month - 1), date.day)
          } else {
            this.errHandler(e.message)
          }
        } else {
          this.$emit('change', e.target.value)
        }
      },
      /**
       * Displays an error notification to the user
       * @param msg   The error message
       */
      errHandler (msg) {
        return this.$message({
          showClose: false,
          message: msg || 'Invalid date format',
          type: 'error'
        })
      },
      /**
       * Parses user-provided date string into Object containing day, month, year,
       * then uses moment.js to create date object and format it into YYYY-MM-DD
       * @return {string}
       */
      formatSingleDateString (dateString, apiDate = false) {
        try {
          return moment(dateString).format('MM-DD-YYYY')
        } catch (e) {
          if (e instanceof Error) {
            this.errHandler(e.message)
          } else {
            console.log(e)
          }
          return ''
        }
      },
      /**
       * Checks whether the user-defined date falls within the range passed via the "allowableRange" prop's "range" property.
       * @param momentDateObject  The momentjs object representation of the user-defined date
       */
      dateIsWithinAllowableRange (momentDateObject) {
        try {
          const { start, end } = this.allowableRange
          let dateAtRangeStart = moment(new Date()).add(start, 'days').format('YYYY-MM-DD')
          let dateAtRangeEnd = moment(new Date()).add(end + 1, 'days').format('YYYY-MM-DD')
          return momentDateObject.isBetween(dateAtRangeStart, dateAtRangeEnd)
        } catch (e) {
          console.log(e)
          return true
        }
      },
      /**
       * Parses user provided date string into Object containing day, month, and year
       * @return {object}
       */
      parseUserDateFormats (dateString) {
        let date = dateString.split('/').join(',').split('-').join(',').split(',')
        if (!date) return ''
        let ret = { day: '', month: '', year: '' }
        switch (date.length) {
          case 3:
            const chkDM = (digits) => (digits && digits.length <= 2)
            const chkYr = (digits, len) => (digits && digits.length === len)
            if (chkDM(date[0]) && chkDM(date[1]) && chkYr(date[2], 4)) {          // MM/DD/YYYY
              ret.year = date[2]
              ret.day = date[1]
              ret.month = date[0]
            } else if (chkDM(date[0]) && chkDM(date[1]) && chkYr(date[2], 2)) {   // MM/DD/YY || M/D/YY
              ret.year = this.parseTwoDigitYear(date[2])
              ret.day = date[1]
              ret.month = date[0]
            } else if (chkYr(date[0], 4) && chkDM(date[1]) && chkDM(date[2])) {   // YYYY/MM/DD
              ret.year = date[0]
              ret.day = date[2]
              ret.month = date[1]
            } else {
              return null
            }
            break
          case 2:
            const defaultToFirstDay = (monthLength) => {
              if (monthLength === 1) {
                return '1'
              } else if (monthLength === 2) {
                return '01'
              }
            }
            if (date[0].length === 4) {         // YYYY/MM
              ret.year = date[0]
              ret.month = date[1]
              ret.day = defaultToFirstDay(ret.month.length)
            } else if (date[0].length <= 2) {   // MM/DD
              ret.month = date[0]
              ret.day = date[1]
            }
            break
          case 1:
            ret = this.handleUndelimitedDateString(date[0])   // YYYYMMDD || MMDDYYYY or inverse
            break
        }
        return ret
      },
      /**
       * Parses user-provided date string when string contains no delimiters (i.e. '/' or '-')
       * Arranges the date string in two possible ways, the first being one where the year
       * might be at the start (yearIsAtStart) and the second being one where the year might
       * be at the end (yearIsAtEnd), then compare yearIsAtStart and yearIsAtEnd to determine
       * which is more likely to be a valid date. If nothing matches, returns null.
       * @param date
       * @return {*}
       */
      handleUndelimitedDateString (date) {
        if (!(date.length === 8 || date.length === 6)) return null
        let yearIsAtStart, yearIsAtEnd, firstTwoDigits, lastTwoDigits
        if (date.length === 8) {
          yearIsAtStart = [   // YYYYMMDD
            date.substring(0, 4),
            date.substring(4, 6),
            date.substring(6, 8)
          ]
          yearIsAtEnd = [     // MMDDYYYY
            date.substring(0, 2),
            date.substring(2, 4),
            date.substring(4, 8)
          ]
          firstTwoDigits = parseInt(yearIsAtStart[0][0] + yearIsAtStart[0][1])
          if (firstTwoDigits > 12 && [19, 20, 21].includes(firstTwoDigits)) {
            return { day: yearIsAtStart[2], month: yearIsAtStart[1], year: yearIsAtStart[0] }
          }
          if (firstTwoDigits <= 12) {
            return { day: yearIsAtEnd[1], month: yearIsAtEnd[0], year: yearIsAtEnd[2] }
          }
        } else if (date.length === 6) {
          yearIsAtEnd = [     // MMDDYY
            date.substring(0, 2),
            date.substring(2, 4),
            date.substring(4, 6)
          ]
          firstTwoDigits = parseInt(yearIsAtEnd[0][0] + yearIsAtEnd[0][1])
          lastTwoDigits = parseInt(yearIsAtEnd[2][0] + yearIsAtEnd[2][1])
          const getCentury = (twoDigitYear) => {
            return twoDigitYear >= 50 && twoDigitYear <= 99 ? 1900 : 2000
          }
          if (firstTwoDigits <= 12) {
            return { day: yearIsAtEnd[1], month: yearIsAtEnd[0], year: getCentury(lastTwoDigits) + lastTwoDigits }
          }
        }
        return null
      },
      /**
       * Parses a two digit year, returning 20xx if year < 50, or 19xx if year > 50
       * @param year
       */
      parseTwoDigitYear (year) {
        return year >= 50 ? parseInt(`19${year}`) : parseInt(`20${year}`)
      },
      onContext (ctx) {
        // The date formatted in the locale, or the `label-no-date-selected` string
        // The following will be an empty string until a valid date is entered
        if (ctx.selectedDate) {
          this.$emit('input', ctx.selectedYMD)
          this.$emit('change')
        }
      }
    },
    computed: {
      /**
       * Returns a boolean indicating whether the provided range in this.allowableRange is valid. Returns true if the
       * range start and end are both numbers and the value of start is less than the value of end.
       */
      allowableRangeIsValid () {
        const range = this.allowableRange
        return range && typeof range.start === 'number' && typeof range.end === 'number' && range.start < range.end
      },
      /**
       * Dynamically sets the style of this field depending on certain conditions such as whether it is disabled
       */
      cellStyle () {
        let style = {}
        if (this.disabled) {
          style.cursor = 'not-allowed'
          style.background = 'none'
        } else if (this.required) {
          style['background-color'] = '#E2F0F5'
        } else {
          delete style.cursor
          delete style.background
        }
        return { ...style, ...this.customStyle }
      },
      displayDate () {
        return this.value ? moment(this.value).format('MM-DD-YYYY') : ''
      }
    },
    watch: {
      value () {
        if (!this.value) {
          this.date = ''
        } else {
          this.date = this.value
        }
      }
    },
    created () {
      this.date = this.formatSingleDateString(this.value)
    }
  }
</script>
<style scoped>
  .date-field {
    background-color: #fff;
    background-image: none;
    border-radius: 4px;
    border: 1px solid #BFCBD9;
    box-sizing: border-box;
    color: #1F2D3D;
    font-size: inherit;
    height: 36px;
    line-height: 1;
    outline: 0;
    padding: 3px 10px;
    transition: border-color .2s cubic-bezier(.645,.045,.355,1);
    width: 100%;
  }
  .b-form-datepicker .dropdown-toggle{
    padding: 4px !important;
    font-size: 11px !important;
  }
</style>
<style>
  .b-form-datepicker .btn-sm {
    padding: 0 0.2rem !important;
    border: none !important;
  }
  #datepicker-input {
    font-size: 12px;
    border: none !important
  }
  .b-calendar-nav {
    margin-left: 3rem;
  }
</style>
  


// WEBPACK FOOTER //
// src/components/InputComponents/DatePicker.vue